import React, { useEffect, useRef, useState } from 'react'
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api'
import {
  BackSquare,
  Copy,
  LocationTick,
  Danger,
  MessageQuestion,
  CloseCircle,
} from 'iconsax-react'
import { Button } from 'primereact/button'
import client from '../client'
import { createRoot } from 'react-dom/client'
import * as DOMPurify from 'dompurify'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import useSound from 'use-sound'
import clickSoundEffect from '../assets/audio/click2.wav'
import { Dialog } from 'primereact/dialog'
import LoadingSpinner from './LoadingSpinner'
import Loading from './Loading'
import { useDispatch } from 'react-redux'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import { postSkipPuzzleRequest } from '../redux/actions/gameActions'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  generateCssClassYes,
  generateCssClassNo,
} from '../utils/confirmDialogClasses'
import { decodeIframeString } from '../utils/decodeIframeString'

const center = {
  lat: -3.745,
  lng: -38.523,
}
const libraries = ['geometry']

function MapGoogle({
  cityName,
  markers,
  mapStyle,
  icon,
  mapHeight,
  checkIfUserIsOnLocation,
  getLocaleIdFromCode,
  showMapControls,
  showMapControlsRef,
  volumeGlobal,
  showScore,
  selectedLanguage,
  isLoadingGps,
  branding,
}) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries,
    // googleMapsApiKey: 'AIzaSyC4XIDUnXB-QX1zYVvcoqbyXBGBHuvbvhs',
  })

  const [map, setMap] = useState(null)
  const prevMarkersRef = useRef([])
  const selectedLanguageRef = useRef(selectedLanguage)

  const [finalLocationHint, setFinalLocationHint] = useState(null)
  const [isUsedFinalLocationHint, setIsUsedFinalLocationHint] = useState(false)
  const [showFinalLocationHintDialog, setShowFinalLocationHintDialog] =
    useState(false)
  const [isLoadingUseFinalLocationHint, setIsLoadingUseFinalLocationHint] =
    useState(false)
  const [cannotReachLocationDialog, setCannotReachLocationDialog] =
    useState(null)

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [clickSound] = useSound(clickSoundEffect, {
    volume: volumeGlobal,
  })

  useEffect(() => {
    selectedLanguageRef.current = selectedLanguage
  }, [selectedLanguage])

  useEffect(() => {
    if (map) {
      //
      window.google.maps.event.addListener(map, 'zoom_changed', function () {
        const zoomChangeBoundsListener = window.google.maps.event.addListener(
          map,
          'bounds_changed',
          function (event) {
            if (this.getZoom() > 15 && this.initialZoom == true) {
              // Change max/min zoom here
              this.setZoom(15)
              this.initialZoom = false
            }
            window.google.maps.event.removeListener(zoomChangeBoundsListener)
          },
        )
      })
      map.initialZoom = true

      //

      clearMarkers(prevMarkersRef.current)

      const svgMarkerSolvedPuzzle = {
        path: 'M-1.547 12l6.563-6.609-1.406-1.406-5.156 5.203-2.063-2.109-1.406 1.406zM0 0q2.906 0 4.945 2.039t2.039 4.945q0 1.453-0.727 3.328t-1.758 3.516-2.039 3.070-1.711 2.273l-0.75 0.797q-0.281-0.328-0.75-0.867t-1.688-2.156-2.133-3.141-1.664-3.445-0.75-3.375q0-2.906 2.039-4.945t4.945-2.039z',
        fillColor: '#4CBB17',
        fillOpacity: 1,
        strokeWeight: 0,
        rotation: 0,
        scale: 2,
        anchor: new window.google.maps.Point(0, 20),
      }

      const bounds = new window.google.maps.LatLngBounds()

      for (let marker of markers) {
        const {
          id,
          longitude,
          latitude,
          pinIcon,
          isPuzzleSolved,
          isLastLocation,
          puzzleName,
          questionId,
          triggerRadius,
          unit,
        } = marker

        if (!isLastLocation) {
          const defaultMarker = `https://${process.env.REACT_APP_STORAGERESOURCENAME}.blob.core.windows.net/static/default-marker2.svg`
          // 'https://sacityhuntdev.blob.core.windows.net/static/default-marker.svg?sp=r&st=2023-01-02T14:30:30Z&se=2043-06-25T21:30:30Z&spr=https&sv=2021-06-08&sr=b&sig=9ZT9%2FKmlRh6a2KgatnUDLVW2%2Bbi39dRst%2BIH6%2FfQskM%3D'

          const markerOnMap = new window.google.maps.Marker({
            position: { lng: longitude, lat: latitude },
            icon:
              pinIcon !== null
                ? isPuzzleSolved
                  ? svgMarkerSolvedPuzzle
                  : {
                      url: pinIcon,
                      scaledSize: new window.google.maps.Size(50, 50),
                      scale: 0.5,
                    }
                : isPuzzleSolved
                ? svgMarkerSolvedPuzzle
                : {
                    url: defaultMarker,
                    scaledSize: new window.google.maps.Size(50, 50),
                    scale: 0.5,
                  },
          })

          markerOnMap.setMap(map)
          prevMarkersRef.current.push(markerOnMap)

          if (!isPuzzleSolved) {
            const circle = new google.maps.Circle({
              map: map,
              fillColor: '#333',
              fillOpacity: 0.3,
              radius: unit === 'm' ? triggerRadius : triggerRadius * 0.3048,
              strokeColor: '#333',
              strokeOpacity: 0.9,
              strokeWeight: 1,
            })

            circle.bindTo('center', markerOnMap, 'position')
          }

          const infowindow = new window.google.maps.InfoWindow()

          window.google.maps.event.addListener(map, 'click', function () {
            infowindow.close()
            if (showMapControlsRef.current) {
              map.setOptions({ disableDefaultUI: false })
            }
          })
          // infowindow.setOptions({maxWidth:500});

          window.google.maps.event.addListener(
            markerOnMap,
            'click',
            async () => {
              // console.log(markerOnMap)
              map.setOptions({ disableDefaultUI: true })
              // infowindow.setOptions({
              //   minWidth: window.innerWidth - 10,
              //   maxWidth: window.innerWidth,
              // })
              // const loadingElement = document.createElement(`div`)
              // loadingElement.style.width = '100%'
              // // loadingElement.style.display = 'flex'
              // // loadingElement.style.justifyContent = 'center'
              // // loadingElement.style.alignItems = 'center'
              // const rootLoading = createRoot(loadingElement)

              // rootLoading.render(<Loading />)
              // infowindow.setContent(loadingElement)
              infowindow.setContent(t('Loading'))

              infowindow.open(map, markerOnMap)
              const token = localStorage.getItem('tokenCityHunt') ?? ''
              const localeId = getLocaleIdFromCode()
              await client
                .get(
                  `/Team/GetPuzzleLocationDescriptionByLocale/${id}/${selectedLanguageRef.current.id}`,
                  {
                    headers: {
                      Authorization: `Bearer ${JSON.parse(token)}`,
                    },
                  },
                )
                // .then((response) => response.json())
                .then((data) => {
                  // let content = ''

                  // if (!data.data.data.descriptionHtml) {
                  //   content += '<p>No description</p>'
                  // } else {
                  //   content += data.data.data.descriptionHtml
                  // }
                  // content +=
                  //   `<button id='btn-on-location${marker.id}' aria-label='Im on location' class='p-button p-component'><svg class='mr-2' xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'><path d='M20.619 8.7c-1.04-4.63-5.08-6.7-8.62-6.7h-.01c-3.53 0-7.56 2.07-8.61 6.69-1.18 5.16 1.98 9.53 4.84 12.29a5.436 5.436 0 0 0 3.78 1.53c1.36 0 2.72-.51 3.77-1.53 2.86-2.76 6.02-7.12 4.85-12.28Zm-5.34.83-4 4c-.15.15-.34.22-.53.22s-.38-.07-.53-.22l-1.5-1.5a.754.754 0 0 1 0-1.06c.29-.29.77-.29 1.06 0l.97.97 3.47-3.47c.29-.29.77-.29 1.06 0 .29.29.29.77 0 1.06Z' fill='#fff'></path></svg><span class='p-button-label p-c'>I'm on location</span></button>`
                  const contentElement = document.createElement(`div`)
                  contentElement.style.height = '100%'
                  contentElement.style.width = '100%'
                  contentElement.style.display = 'flex'
                  contentElement.style.justifyContent = 'center'
                  const root = createRoot(contentElement)

                  // console.log(marker)

                  root.render(
                    <div
                      // className="flex flex-column"
                      style={{
                        height: '100%',
                        width: '100%',
                        // paddingBottom: '10%',
                      }}
                    >
                      {/* {puzzleName ? (
                        <h1 style={{ color: '#333' }}>{puzzleName}</h1>
                      ) : null} */}
                      {data.data.data.descriptionHtml ? (
                        <div
                          style={{ textAlign: 'initial', height: 'auto' }}
                          className="img-full-width ql-editor mb-3"
                          dangerouslySetInnerHTML={sanitizedData(
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            decodeIframeString(
                              data.data.data.descriptionHtml ?? '',
                            ) ?? '',
                          )}
                        ></div>
                      ) : null}
                      {!isPuzzleSolved ? (
                        <>
                          <Button
                            label={t('ImOnLocation')}
                            className="w-full sign-in-btn p-button-rounded"
                            // icon={
                            //   <LocationTick
                            //     // size="55"
                            //     color="#fff"
                            //     variant="Bold"
                            //     className="mr-1"
                            //   />
                            // }
                            icon={
                              <>
                                <div
                                  className="p-2 p-button-icon p-button-icon-right rounded-icon-style"
                                  style={{
                                    backgroundColor:
                                      branding?.buttonPrimary.textColor,
                                  }}
                                >
                                  {/* <div className="p-2 p-button-icon p-c p-button-icon-right border-circle bg-white"> */}
                                </div>
                                <LocationTick
                                  color={
                                    branding?.buttonPrimary.backgroundColor
                                  }
                                  variant="Bold"
                                  className="icon-center"
                                  // className="p-button-icon p-c p-button-icon-right"
                                />
                              </>
                            }
                            onClick={() => {
                              checkIfUserIsOnLocation(marker)
                              clickSound()
                            }}
                            loading={isLoadingGps}
                            style={{
                              backgroundColor:
                                branding?.buttonPrimary.backgroundColor,
                              color: branding?.buttonPrimary.textColor,
                            }}
                          />
                          <CopyToClipboard
                            text={`${latitude}, ${longitude}`}
                            className="w-full mt-3"
                          >
                            <div className="copy-area">
                              <Button
                                className="p-button-outlined p-button-rounded w-full"
                                // className="p-button-outlined p-button-rounded w-full sign-in-btn"
                                // style={{ background: '#c8d5db', color: '#333' }}
                                label={t('CopyCoords')}
                                // icon="pi pi-copy"
                                // icon={
                                //   <Copy
                                //     // size="55"
                                //     color="#DF6436"
                                //     variant="Bold"
                                //     className="mr-1"
                                //   />
                                // }
                                icon={
                                  <>
                                    <div
                                      className="p-2 p-button-icon p-button-icon-right rounded-icon-style-secondary"
                                      style={{
                                        backgroundColor:
                                          branding?.buttonSecondary.textColor,
                                      }}
                                    >
                                      {/* <div className="p-2 p-button-icon p-c p-button-icon-right border-circle bg-white"> */}
                                    </div>
                                    <Copy
                                      color={
                                        branding?.buttonSecondary
                                          .backgroundColor
                                      }
                                      variant="Bold"
                                      className="icon-center"
                                      // className="p-button-icon p-c p-button-icon-right"
                                    />
                                  </>
                                }
                                style={{
                                  backgroundColor:
                                    branding?.buttonSecondary.backgroundColor,
                                  color: branding?.buttonSecondary.textColor,
                                  border: `2px solid ${branding?.buttonSecondary.borderColor}`,
                                }}
                                onClick={() => clickSound()}
                              />
                            </div>
                          </CopyToClipboard>
                          <p
                            onClick={() =>
                              setCannotReachLocationDialog({ questionId })
                            }
                            style={{
                              marginTop: '1rem',
                              color: '#0000EE',
                              textDecoration: 'underline',
                            }}
                          >
                            {t('CannotReachLocation')}
                          </p>
                          <Button
                            label={t('BackToMap')}
                            className="p-button-outlined p-button-rounded w-full mt-3"
                            style={{
                              paddingRight: '3rem',
                              width: '84%',
                              backgroundColor:
                                branding?.buttonPrimary.backgroundColor,
                              color: branding?.buttonPrimary.textColor,
                            }}
                            icon={
                              <>
                                <div
                                  className="p-2 p-button-icon p-button-icon-right rounded-icon-style"
                                  style={{
                                    backgroundColor:
                                      branding?.buttonPrimary.textColor,
                                  }}
                                />
                                <CloseCircle
                                  color={
                                    branding?.buttonPrimary.backgroundColor
                                  }
                                  variant="Bold"
                                  className="icon-center"
                                />
                              </>
                            }
                            onClick={() => {
                              clickSound()
                              infowindow.close()
                            }}
                            loading={isLoadingGps}
                          />
                        </>
                      ) : (
                        <h3>{t('YouSolvedPuzzle')}</h3>
                      )}
                    </div>,
                  )
                  infowindow.setOptions({
                    minWidth: window.innerWidth - 10,
                    maxWidth: window.innerWidth,
                  })
                  infowindow.setContent(contentElement)
                  window.google.maps.event.addListener(
                    infowindow,
                    'closeclick',
                    function () {
                      map.setOptions({ disableDefaultUI: false })
                      clickSound()
                    },
                  )
                  // setLoading(false)

                  // infowindow.setContent(content)
                  // const element = document.getElementById(
                  //   `btn-on-location${marker.id}`,
                  // )
                  // element.addEventListener('click', onLocation)
                })
              // .then((json) => infowindow.setContent(json.title))
            },
          )

          bounds.extend(markerOnMap.getPosition())
        } else if (isLastLocation && isPuzzleSolved) {
          //

          const markerFinalSolved = new window.google.maps.Marker({
            position: { lng: longitude, lat: latitude },
            icon: svgMarkerSolvedPuzzle,
          })

          markerFinalSolved.setMap(map)
          prevMarkersRef.current.push(markerFinalSolved)

          const infowindow = new window.google.maps.InfoWindow()

          window.google.maps.event.addListener(map, 'click', function () {
            infowindow.close()
            if (showMapControls) {
              map.setOptions({ disableDefaultUI: false })
            }
          })

          window.google.maps.event.addListener(
            markerFinalSolved,
            'click',
            async () => {
              map.setOptions({ disableDefaultUI: true })
              // const loadingElement = document.createElement(`div`)
              // const rootLoading = createRoot(contentElement)

              // rootLoading.render(<Loading />)
              // infowindow.setContent(loadingElement)
              infowindow.setContent(t('Loading'))
              infowindow.open(map, markerFinalSolved)
              const token = localStorage.getItem('tokenCityHunt') ?? ''
              const localeId = getLocaleIdFromCode()
              await client
                .get(
                  `/Team/GetPuzzleLocationDescriptionByLocale/${id}/${selectedLanguageRef.current.id}`,
                  {
                    headers: {
                      Authorization: `Bearer ${JSON.parse(token)}`,
                    },
                  },
                )
                .then((data) => {
                  const contentElement = document.createElement(`div`)
                  contentElement.style.height = '100%'
                  contentElement.style.width = '100%'
                  contentElement.style.display = 'flex'
                  contentElement.style.justifyContent = 'center'
                  const root = createRoot(contentElement)

                  root.render(
                    <div
                      // className="flex flex-column"
                      style={{
                        height: '100%',
                        width: '100%',
                        // marginBottom: '10%',
                      }}
                    >
                      {puzzleName ? (
                        <h1 style={{ color: '#333' }}>{puzzleName}</h1>
                      ) : null}
                      {data.data.data.descriptionHtml ? (
                        <div
                          style={{ height: '100%', textAlign: 'initial' }}
                          className="img-full-width ql-editor mb-3"
                          dangerouslySetInnerHTML={sanitizedData(
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            decodeIframeString(
                              data.data.data.descriptionHtml ?? '',
                            ) ?? '',
                          )}
                        ></div>
                      ) : null}
                      <h3>{t('YouSolvedPuzzle')}</h3>
                    </div>,
                  )
                  infowindow.setOptions({
                    minWidth: window.innerWidth - 10,
                    maxWidth: window.innerWidth,
                  })
                  infowindow.setContent(contentElement)
                  window.google.maps.event.addListener(
                    infowindow,
                    'closeclick',
                    function () {
                      map.setOptions({ disableDefaultUI: false })
                    },
                  )
                })
            },
          )

          bounds.extend(markerFinalSolved.getPosition())
        }
      }
      const options = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const currentPosition = new google.maps.LatLng(
            pos.coords.latitude,
            pos.coords.longitude,
          )
          bounds.extend(currentPosition)
          // console.log(bounds)
          map.fitBounds(bounds)
        },
        (err) => {
          console.log(err)
        },
        options,
      )
    }
  }, [map, markers])

  useEffect(() => {
    if (map) {
      map.setOptions({ disableDefaultUI: !showMapControls })
    }
  }, [map, showMapControls])

  const showLocationHintIfFinalPuzzle = async () => {
    const unsolvedPuzzles = markers.filter((el) => !el.isPuzzleSolved)
    if (unsolvedPuzzles.length === 1 && unsolvedPuzzles[0].isLastLocation) {
      const localeId = getLocaleIdFromCode()
      const token = localStorage.getItem('tokenCityHunt') ?? ''

      await client
        .get(
          `/Team/GetFinalPuzzleLocationHint?locationId=${unsolvedPuzzles[0].id}&localeId=${selectedLanguage.id}`,
          {
            headers: {
              Authorization: `Bearer ${JSON.parse(token)}`,
            },
          },
        )
        .then((response) => {
          setFinalLocationHint(response.data.data)
          if (response.data.data.usedFinalLocationHint) {
            setIsUsedFinalLocationHint(true)
          }
          setShowFinalLocationHintDialog(true)
        })
    }
  }

  const sanitizedData = (data) => ({
    __html: DOMPurify.sanitize(data, {
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['src', 'class', 'allowfullscreen', 'frameborder'],
    }),
  })

  const clearMarkers = (markers) => {
    for (let marker of markers) {
      marker.setMap(null)
    }
    prevMarkersRef.current = []
  }

  const onLoad = React.useCallback(function callback(map) {
    // console.log(map)
    const geocoder = new window.google.maps.Geocoder()

    geocoder.geocode(
      {
        address: cityName,
      },
      function (results, status) {
        // if (status == maps.GeocoderStatus.OK) {
        if (status == window.google.maps.GeocoderStatus.OK) {
          const lat = results[0].geometry.location.lat()
          const lng = results[0].geometry.location.lng()
          // console.log({ lng, lat })
          // setCoords({ lat, lng })
          // This is just an example of getting and using the map instance!!! don't just blindly copy!
          const bounds = new window.google.maps.LatLngBounds({ lng, lat })
          // const bounds = new window.google.maps.LatLngBounds(center)
          map.fitBounds(bounds)
          map.setZoom(14)
          // map.zoom = 5
          // console.log(map)

          setMap(map)

          const currentLocationIcon = {
            url: '/assets/layout/images/pulseDot.svg',
            // url: '/assets/layout/images/pulseDot.svg',
            // url: '/assets/layout/images/bluedot.png',
            scaledSize: new google.maps.Size(25, 25),
            scale: 1,
          }

          const marker = new window.google.maps.Marker({
            position: { lng, lat },
            // icon: '/assets/layout/images/current-position.svg',
            icon: currentLocationIcon,
            zIndex: 9999,
          })

          marker.setMap(map)

          //* Current position
          trackLocation({
            onSuccess: ({
              coords: { latitude: lat, longitude: lng, accuracy: accuracy },
            }) => {
              // console.log(accuracy)
              marker.setPosition({ lat, lng })
            },
            onError: (err) => console.log(err),
          })
        } else {
          alert('Something got wrong ' + status)
        }
      },
    )
  }, [])

  const trackLocation = ({
    onSuccess,
    onError = () => {
      console.log('error')
    },
  }) => {
    if ('geolocation' in navigator === false) {
      return onError(new Error('Geolocation is not supported by your browser.'))
    }
    // Use watchPosition instead.
    return navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    })
  }

  // console.log(coords)

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null)
  }, [])

  const renderLastLocationCheckBtn = () => {
    const unsolvedPuzzles = markers.filter((el) => !el.isPuzzleSolved)

    if (unsolvedPuzzles.length > 1) {
      return null
    } else if (unsolvedPuzzles.length === 1) {
      if (unsolvedPuzzles[0].isLastLocation) {
        // showScore
        return (
          <div
            className="checkLocationBtn flex justify-content-between"
            style={{ width: '94%', bottom: showScore ? '7.2rem' : '1.5rem' }}
          >
            <Button
              label={t('ImOnLocation')}
              className="p-button-rounded div"
              style={{
                paddingRight: '3rem',
                width: '84%',
                backgroundColor: branding?.buttonPrimary.backgroundColor,
                color: branding?.buttonPrimary.textColor,
              }}
              // className="p-button-rounded div w-full"
              // icon={
              //   <LocationTick
              //     // size="55"
              //     color="#fff"
              //     variant="Bold"
              //     className="mr-2"
              //   />
              // }
              icon={
                <>
                  <div
                    className="p-2 p-button-icon p-button-icon-right rounded-icon-style"
                    style={{
                      backgroundColor: branding?.buttonPrimary.textColor,
                    }}
                  >
                    {/* <div className="p-2 p-button-icon p-c p-button-icon-right border-circle bg-white"> */}
                  </div>
                  <LocationTick
                    color={branding?.buttonPrimary.backgroundColor}
                    variant="Bold"
                    className="icon-center"
                    // className="p-button-icon p-c p-button-icon-right"
                  />
                </>
              }
              onClick={() => {
                checkIfUserIsOnLocation(unsolvedPuzzles[0])
                clickSound()
              }}
              loading={isLoadingGps}
            />
            <Button
              onClick={() => showLocationHintIfFinalPuzzle()}
              icon={
                <MessageQuestion
                  color={branding?.buttonPrimary.textColor}
                  variant="Bold"
                />
              }
              className="p-button-rounded p-button-primary"
              style={{
                backgroundColor: branding?.buttonPrimary.backgroundColor,
                color: branding?.buttonPrimary.textColor,
              }}
            />
          </div>
        )
      }
    }

    return null
  }

  const useFinalLocationHint = async () => {
    setIsLoadingUseFinalLocationHint(true)
    const token = localStorage.getItem('tokenCityHunt') ?? ''

    await client
      .get(`/Team/UseFinalLocationHint`, {
        headers: {
          Authorization: `Bearer ${JSON.parse(token)}`,
        },
      })
      .then((response) => {
        setIsUsedFinalLocationHint(true)
        setIsLoadingUseFinalLocationHint(false)
      })
  }

  const renderLastLocationDialog = () => {
    if (showFinalLocationHintDialog) {
      return (
        <Dialog
          header={t('FinalLocationHint')}
          visible={showFinalLocationHintDialog}
          style={{ width: '90vw' }}
          onHide={() => setShowFinalLocationHintDialog(false)}
          headerStyle={{
            backgroundColor: branding?.modals.backgroundColor,
            color: branding?.modals.textColor,
          }}
        >
          {renderLastLocationDialogData()}
        </Dialog>
      )
    }

    return null
  }

  const renderCannotReachLocationDialog = () => {
    if (cannotReachLocationDialog && cannotReachLocationDialog.questionId) {
      return (
        <Dialog
          header={t('YouCannotReachLocation')}
          visible={cannotReachLocationDialog}
          style={{ width: '90vw' }}
          onHide={() => setCannotReachLocationDialog(null)}
          headerStyle={{
            backgroundColor: branding?.modals.backgroundColor,
            color: branding?.modals.textColor,
          }}
        >
          <ConfirmDialog />
          <p>{t('FinalLocationHintDesc')}</p>
          <Button
            label={t('SkipPuzzle')}
            onClick={() =>
              confirmSkipHandler(cannotReachLocationDialog.questionId)
            }
            // onClick={() => setVisible(true)}
            type="button"
            icon={
              <>
                <div
                  className="p-2 p-button-icon p-button-icon-right rounded-icon-style-secondary"
                  style={{
                    backgroundColor: branding?.buttonSecondary.textColor,
                  }}
                >
                  {/* <div className="p-2 p-button-icon p-c p-button-icon-right border-circle bg-white"> */}
                </div>
                <BackSquare
                  color={branding?.buttonSecondary.backgroundColor}
                  variant="Bold"
                  className="icon-center"
                  // className="p-button-icon p-c p-button-icon-right"
                />
              </>
            }
            // icon={
            //   <BackSquare color="#DF6436" variant="Bold" className="mr-1" />
            // }
            className="p-button-rounded p-button-outlined w-full zoominleft animation-duration-1000"
            // className="p-button-rounded p-button-outlined w-full sign-in-btn zoominleft animation-duration-1000"
            style={{
              backgroundColor: branding?.buttonSecondary.backgroundColor,
              color: branding?.buttonSecondary.textColor,
              border: `2px solid ${branding?.buttonSecondary.borderColor}`,
            }}
          />
        </Dialog>
      )
    }

    return null
  }

  const accept = (questionId) => {
    // clickSound()
    const localeId = getLocaleIdFromCode()
    dispatch(
      postSkipPuzzleRequest({
        questionId,
        localeId: selectedLanguage.id,
        skipFrom: 'map',
        navigate,
      }),
    )
    setCannotReachLocationDialog(null)
    // setSkippedPuzzle(true)
  }

  const confirmSkipHandler = (id) => {
    // clickSound()
    confirmDialog({
      message: t('SkipPuzzleConfirm'),
      header: t('Confirmation'),
      icon: <Danger size="3rem" color="#333" variant="Bold" />,
      // icon: 'pi pi-exclamation-triangle',
      accept: () => accept(id),
      acceptLabel: t('Yes'),
      rejectLabel: t('No'),
      acceptClassName: generateCssClassYes(branding),
      rejectClassName: generateCssClassNo(branding),
      style: {
        width: '90%',
      },
      headerStyle: {
        background: branding?.modals.backgroundColor,
      },
      onHide: setCannotReachLocationDialog(null),
    })
  }

  const renderLastLocationDialogData = () => {
    if (isLoadingUseFinalLocationHint) {
      return <LoadingSpinner />
    }

    if (isUsedFinalLocationHint) {
      return (
        <div
          style={{ height: '100%', textAlign: 'initial' }}
          className="img-full-width ql-editor mb-3"
          dangerouslySetInnerHTML={sanitizedData(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            decodeIframeString(finalLocationHint.finalLocationHintHtml ?? '') ??
              '',
          )}
        ></div>
      )
    } else {
      return (
        <div>
          <p>{t('LocationHintConfirm')}</p>

          <Button
            label={t('UseLocationHint')}
            className="p-button-rounded div w-full"
            onClick={useFinalLocationHint}
            style={{
              backgroundColor: branding?.buttonPrimary.backgroundColor,
              color: branding?.buttonPrimary.textColor,
            }}
          />
        </div>
      )
    }
  }
  // const onLoadMarker = (marker) => {
  //   console.log('marker: ', marker)
  // }

  return isLoaded ? (
    <GoogleMap
      // mapContainerStyle={containerStyle}
      mapContainerStyle={{
        width: '100%',
        height: `calc(${window.innerHeight - mapHeight}px)`,
      }}
      options={{
        styles: mapStyle,
        streetViewControl: false,
      }}
      center={center}
      // zoom={10}
      onLoad={onLoad}
      onUnmount={onUnmount}
      // onClick={handleMapClick}
    >
      {/* Child components, such as markers, info windows, etc. */}
      {renderLastLocationCheckBtn()}
      {/* {renderLastLocationHintBtn()} */}
      {renderLastLocationDialog()}
      {renderCannotReachLocationDialog()}
    </GoogleMap>
  ) : (
    <></>
  )
}

export default MapGoogle
